// frontend/src/pages/partner/FacturationPartner.jsx
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DocumentArrowUpIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ClockIcon,
  BanknotesIcon,
  XCircleIcon,
  ArrowPathIcon,
  ExclamationCircleIcon,
  CalendarDaysIcon,
  ChartBarIcon,
} from '@heroicons/react/24/solid';
import API from '../../services/api';
import './FacturationPartner.css';

const STATUS_CONFIG = {
  submitted:  { label: 'Soumise',        color: '#f59e0b', bg: '#fffbeb', border: '#fde68a', icon: <ClockIcon style={{ width: 14, height: 14 }} /> },
  processing: { label: 'En traitement',  color: '#06b6d4', bg: '#ecfeff', border: '#a5f3fc', icon: <ArrowPathIcon style={{ width: 14, height: 14 }} /> },
  paid:       { label: 'Virée',          color: '#10b981', bg: '#f0fdf4', border: '#bbf7d0', icon: <CheckCircleIcon style={{ width: 14, height: 14 }} /> },
  rejected:   { label: 'Rejetée',        color: '#ef4444', bg: '#fef2f2', border: '#fecaca', icon: <XCircleIcon style={{ width: 14, height: 14 }} /> },
  pending:    { label: 'À soumettre',    color: '#94a3b8', bg: '#f8fafc', border: '#e2e8f0', icon: <DocumentTextIcon style={{ width: 14, height: 14 }} /> },
};

const fmt      = n => Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 });
const fmtMonth = m => {
  if (!m) return '';
  const [y, mo] = m.split('-');
  return new Date(y, mo - 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
};
const currentMonth = () => new Date().toISOString().slice(0, 7);
const prevMonth = () => {
  const d = new Date(); d.setMonth(d.getMonth() - 1);
  return d.toISOString().slice(0, 7);
};

export default function FacturationPartner() {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [submitMonth, setSubmitMonth] = useState(currentMonth);
  const [file, setFile]           = useState(null);
  const [submitting, setSubmitting]   = useState(false);
  const [generating, setGenerating]   = useState(false);
  const [msg, setMsg]             = useState(null);
  const fileRef = useRef();
  const timerRef = useRef();

  const fetchData = async () => {
    try {
      const res = await API.get('/partner/billing');
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); return () => clearTimeout(timerRef.current); }, []);

  const showMsg = (type, text) => {
    setMsg({ type, text });
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setMsg(null), 5000);
  };

  const handleGenerate = async () => {
    if (!submitMonth) return showMsg('error', 'Sélectionnez le mois');
    if (!monthRow) return showMsg('error', 'Aucune transaction B2C pour ce mois');
    setGenerating(true);
    try {
      const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiBase}/partner/billing/generate/${submitMonth}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Erreur de génération');
      }
      const blob = await response.blob();
      // Auto-remplir la zone upload avec le PDF généré
      const filename = `facture-INV-${submitMonth.replace('-', '')}.pdf`;
      const generatedFile = new File([blob], filename, { type: 'application/pdf' });
      setFile(generatedFile);
      // Télécharger également pour que le partenaire puisse garder une copie
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      showMsg('success', 'Facture générée et prête à soumettre');
    } catch (e) {
      showMsg('error', e.message || 'Erreur lors de la génération');
    } finally { setGenerating(false); }
  };

  const handleSubmit = async () => {
    if (!file) return showMsg('error', 'Veuillez sélectionner votre facture PDF');
    if (!submitMonth) return showMsg('error', 'Sélectionnez le mois');
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append('invoice', file);
      form.append('month', submitMonth);
      await API.post('/partner/billing/submit', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      showMsg('success', 'Facture soumise ! Runit-FR traitera le virement sous 30 jours.');
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      await fetchData();
    } catch (e) {
      showMsg('error', e.response?.data?.error || 'Erreur lors de la soumission');
    } finally { setSubmitting(false); }
  };

  // Find the row for the selected submit month to show current commission
  const monthRow = data?.months?.find(m => m.month === submitMonth);

  if (loading) return (
    <div className="fp-loading">
      <div className="fp-spinner" />
      <p>Chargement de votre facturation…</p>
    </div>
  );

  return (
    <div className="fp-page">

      {/* ── Header ── */}
      <motion.div className="fp-header" initial={{ opacity: 0, y: -18 }} animate={{ opacity: 1, y: 0 }}>
        <div className="fp-header-orb fp-header-orb-1" />
        <div className="fp-header-orb fp-header-orb-2" />
        <div className="fp-header-left">
          <div className="fp-header-icon">
            <BanknotesIcon style={{ width: 28, height: 28 }} />
          </div>
          <div>
            <h1>Facturation mensuelle</h1>
            <p>Soumettez votre facture — Runit-FR vous vire votre commission sous 30 jours</p>
          </div>
        </div>
        <div className="fp-header-pill">
          <ChartBarIcon style={{ width: 16, height: 16 }} />
          Commission : <strong>{Number(data?.commission_rate || 0).toFixed(0)} %</strong>
        </div>
      </motion.div>

      {/* ── Alert ── */}
      <AnimatePresence>
        {msg && (
          <motion.div
            className={`fp-alert fp-alert--${msg.type}`}
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
          >
            {msg.type === 'success'
              ? <CheckCircleIcon style={{ width: 18, height: 18, flexShrink: 0 }} />
              : <ExclamationCircleIcon style={{ width: 18, height: 18, flexShrink: 0 }} />}
            {msg.text}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Submit card ── */}
      <motion.div className="fp-card" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0, transition: { delay: 0.08 } }}>
        <h3><DocumentArrowUpIcon style={{ width: 18, height: 18 }} /> Soumettre ma facture</h3>

        <div className="fp-submit-grid">
          {/* Month picker */}
          <div className="fp-field">
            <label><CalendarDaysIcon style={{ width: 14, height: 14 }} /> Mois de prestation</label>
            <input
              type="month"
              value={submitMonth}
              onChange={e => setSubmitMonth(e.target.value)}
              className="fp-input"
            />
          </div>

          {/* Commission preview */}
          {monthRow ? (
            <div className="fp-commission-preview">
              <div className="fp-cp-row">
                <span>CA B2C généré</span>
                <strong>{fmt(monthRow.ca_eur)} EUR</strong>
              </div>
              <div className="fp-cp-row">
                <span>Taux commission</span>
                <strong>{Number(data.commission_rate).toFixed(0)} %</strong>
              </div>
              <div className="fp-cp-row fp-cp-total">
                <span>Commission due</span>
                <strong className="fp-amount">{fmt(monthRow.commission_due)} EUR</strong>
              </div>
              <p className="fp-cp-note">
                Indiquez ce montant sur votre facture à l'ordre de <strong>Runit-FR</strong>
              </p>
            </div>
          ) : (
            <div className="fp-commission-preview fp-cp-empty">
              <p>Aucune transaction B2C pour {fmtMonth(submitMonth)}</p>
              <span>Sélectionnez un mois avec de l'activité</span>
            </div>
          )}
        </div>

        {/* Generate button */}
        <div className="fp-generate-row">
          <div className="fp-generate-info">
            <DocumentTextIcon style={{ width: 16, height: 16, color: '#06b6d4', flexShrink: 0 }} />
            <span>Générez automatiquement votre facture PDF depuis les données du système</span>
          </div>
          <motion.button
            className="fp-btn-generate"
            onClick={handleGenerate}
            disabled={generating || !submitMonth}
            whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
          >
            {generating
              ? <><ArrowPathIcon style={{ width: 15, height: 15 }} className="fp-spin" /> Génération…</>
              : <><DocumentArrowUpIcon style={{ width: 15, height: 15 }} /> Générer ma facture PDF</>
            }
          </motion.button>
        </div>

        <div className="fp-divider-or"><span>ou importez votre propre facture</span></div>

        {/* File upload */}
        <div className="fp-upload-zone" onClick={() => fileRef.current?.click()}>
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf"
            style={{ display: 'none' }}
            onChange={e => setFile(e.target.files[0] || null)}
          />
          {file ? (
            <div className="fp-file-selected">
              <DocumentTextIcon style={{ width: 22, height: 22, color: '#06b6d4' }} />
              <span>{file.name}</span>
              <button className="fp-file-remove" onClick={e => { e.stopPropagation(); setFile(null); if (fileRef.current) fileRef.current.value = ''; }}>×</button>
            </div>
          ) : (
            <>
              <DocumentArrowUpIcon style={{ width: 28, height: 28, color: '#06b6d4', marginBottom: 8 }} />
              <p>Cliquez pour sélectionner votre facture PDF</p>
              <span>Format PDF uniquement · Max 5 Mo</span>
            </>
          )}
        </div>

        <div className="fp-submit-footer">
          <div className="fp-info-tip">
            <ClockIcon style={{ width: 16, height: 16, flexShrink: 0 }} />
            Délai de virement : <strong>30 jours</strong> après réception de la facture (délai chargeback Stripe)
          </div>
          <motion.button
            className="fp-btn-submit"
            onClick={handleSubmit}
            disabled={submitting || !file || !submitMonth || !monthRow}
            whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
          >
            {submitting
              ? <><ArrowPathIcon style={{ width: 16, height: 16 }} className="fp-spin" /> Envoi en cours…</>
              : <><DocumentArrowUpIcon style={{ width: 16, height: 16 }} /> Soumettre la facture</>
            }
          </motion.button>
        </div>
      </motion.div>

      {/* ── History table ── */}
      <motion.div className="fp-card" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0, transition: { delay: 0.14 } }}>
        <h3><ClockIcon style={{ width: 18, height: 18 }} /> Historique de facturation</h3>

        {(!data?.months || data.months.length === 0) ? (
          <div className="fp-empty">
            <DocumentTextIcon style={{ width: 36, height: 36, color: '#cbd5e1' }} />
            <p>Aucune activité facturable pour le moment</p>
            <span>Vos mois d'activité apparaîtront ici dès la première transaction B2C</span>
          </div>
        ) : (
          <div className="fp-table-wrap">
            <table className="fp-table">
              <thead>
                <tr>
                  <th>Mois</th>
                  <th>Nb Tx</th>
                  <th>CA B2C (EUR)</th>
                  <th>Commission due</th>
                  <th>Statut facture</th>
                  <th>Soumise le</th>
                  <th>Virement prévu</th>
                  <th>Réf. virement</th>
                </tr>
              </thead>
              <tbody>
                {data.months.map((row) => {
                  const st = STATUS_CONFIG[row.invoice_status || 'pending'];
                  return (
                    <tr key={row.month}>
                      <td className="fp-month-cell">{fmtMonth(row.month)}</td>
                      <td>{row.nb_tx}</td>
                      <td><strong>{fmt(row.ca_eur)}</strong></td>
                      <td><span className="fp-amount-cell">{fmt(row.commission_due)} €</span></td>
                      <td>
                        <span className="fp-badge" style={{ color: st.color, background: st.bg, borderColor: st.border }}>
                          {st.icon} {st.label}
                        </span>
                        {row.invoice_status === 'rejected' && row.admin_note && (
                          <div className="fp-reject-note" title={row.admin_note}>
                            <ExclamationCircleIcon style={{ width: 13, height: 13 }} /> {row.admin_note}
                          </div>
                        )}
                      </td>
                      <td className="fp-date-cell">
                        {row.submitted_at ? new Date(row.submitted_at).toLocaleDateString('fr-FR') : '—'}
                      </td>
                      <td className="fp-date-cell">
                        {row.due_date ? new Date(row.due_date).toLocaleDateString('fr-FR') : '—'}
                      </td>
                      <td>
                        {row.virement_ref
                          ? <span className="fp-ref">{row.virement_ref}</span>
                          : <span style={{ color: '#cbd5e1' }}>—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>
    </div>
  );
}
